import {
  createAsyncThunk,
  createSlice,
} from '@reduxjs/toolkit';

const initialState = {
  status: 'idle',
  error: null,
};

// Async thunks
export const fetchAuthors = createAsyncThunk(
  'authors/fetchAuthors',
  async ({ namespace, params }, { extra }) => {
    const { authorsApi } = extra;
    return authorsApi.getAuthors(namespace, params);
  },
);

export const fetchAuthorById = createAsyncThunk(
  'authors/fetchAuthorById',
  async ({ namespace, authorId }, { extra }) => {
    const { authorsApi } = extra;
    return authorsApi.getAuthorById(namespace, authorId);
  },
);

export const createAuthor = createAsyncThunk(
  'authors/createAuthor',
  async ({ author }, { extra }) => {
    const { authorsApi } = extra;
    return authorsApi.createAuthor(author);
  },
);

export const updateAuthor = createAsyncThunk(
  'authors/updateAuthor',
  async ({ namespace, authorId, patch }, { extra }) => {
    const { authorsApi } = extra;
    return authorsApi.updateAuthor(namespace, authorId, patch);
  },
);

export const replaceAuthor = createAsyncThunk(
  'authors/replaceAuthor',
  async ({ namespace, authorId, author }, { extra }) => {
    const { authorsApi } = extra;
    return authorsApi.replaceAuthor(namespace, authorId, author);
  },
);

export const deleteAuthor = createAsyncThunk(
  'authors/deleteAuthor',
  async ({ namespace, authorId }, { extra }) => {
    const { authorsApi } = extra;
    await authorsApi.deleteAuthor(namespace, authorId);
    return authorId;
  },
);

export const bookmarkAuthor = createAsyncThunk(
  'authors/bookmarkAuthor',
  async ({ authorId }, { extra }) => {
    const { authorsApi } = extra;
    return authorsApi.bookmarkAuthor(authorId);
  },
);

export const shareAuthor = createAsyncThunk(
  'authors/shareAuthor',
  async ({ authorId }, { extra }) => {
    const { authorsApi } = extra;
    return authorsApi.shareAuthor(authorId);
  },
);

export const mergeAuthors = createAsyncThunk(
  'authors/mergeAuthors',
  async ({ namespace, targetAuthorId, sourceAuthorIds }, { extra }) => {
    const { authorsApi } = extra;
    return authorsApi.mergeAuthors(namespace, targetAuthorId, sourceAuthorIds);
  },
);

export const fetchAuthorSuggestions = createAsyncThunk(
  'authors/fetchSuggestions',
  async ({ namespace, searchTerm }, { extra }) => {
    const { authorsApi } = extra;
    return authorsApi.getSuggestions(namespace, searchTerm);
  },
);

const authorsSlice = createSlice({
  name: 'authors',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchAuthors.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(fetchAuthors.fulfilled, (state) => {
        state.status = 'succeeded';
      })
      .addCase(fetchAuthors.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message;
      })
      .addCase(fetchAuthorById.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(fetchAuthorById.fulfilled, (state) => {
        state.status = 'succeeded';
      })
      .addCase(fetchAuthorById.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message;
      })
      .addCase(createAuthor.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(createAuthor.fulfilled, (state) => {
        state.status = 'succeeded';
      })
      .addCase(createAuthor.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message;
      })
      .addCase(updateAuthor.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(updateAuthor.fulfilled, (state) => {
        state.status = 'succeeded';
      })
      .addCase(updateAuthor.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message;
      })
      .addCase(replaceAuthor.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(replaceAuthor.fulfilled, (state) => {
        state.status = 'succeeded';
      })
      .addCase(replaceAuthor.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message;
      })
      .addCase(deleteAuthor.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(deleteAuthor.fulfilled, (state) => {
        state.status = 'succeeded';
      })
      .addCase(deleteAuthor.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message;
      })
      .addCase(bookmarkAuthor.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(bookmarkAuthor.fulfilled, (state) => {
        state.status = 'succeeded';
      })
      .addCase(bookmarkAuthor.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message;
      })
      .addCase(shareAuthor.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(shareAuthor.fulfilled, (state) => {
        state.status = 'succeeded';
      })
      .addCase(shareAuthor.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message;
      })
      .addCase(mergeAuthors.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(mergeAuthors.fulfilled, (state) => {
        state.status = 'succeeded';
      })
      .addCase(mergeAuthors.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message;
      });
  },
});

export default authorsSlice.reducer;
